import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from './Donations.module.css';

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function DonationsPage({ user }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(searchParams.get('month') || 'dummy');
  const [accumulative, setAccumulative] = useState(searchParams.get('acc') !== 'false');
  const [calendarType, setCalendarType] = useState(searchParams.get('cal') || 'hebrew');
  const [loading, setLoading] = useState(true);
  const [dailyPopup, setDailyPopup] = useState(null);
  const [swipeDir, setSwipeDir] = useState(null); // 'left' | 'right' for animation
  const touchStart = useRef(null);
  const containerRef = useRef(null);

  // Sync state to URL
  useEffect(() => {
    const params = {};
    if (month && month !== 'dummy') params.month = month;
    if (!accumulative) params.acc = 'false';
    if (calendarType !== 'hebrew') params.cal = calendarType;
    setSearchParams(params, { replace: true });
  }, [month, accumulative, calendarType, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ month, accumulative: String(accumulative), calendarType });
      const res = await fetch(`${BASE_URL}/api/donations/monthly?${params}`, {
        headers: { 'Authorization': `Bearer ${user.credential}` },
      });
      const json = await res.json();
      setData(json);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [month, accumulative, calendarType, user.credential]);

  useEffect(() => { load(); }, [load]);

  const goNext = () => {
    if (!data) return;
    setSwipeDir('left');
    setTimeout(() => { setMonth(data.nextMonth); setSwipeDir(null); }, 150);
  };

  const goPrev = () => {
    if (!data) return;
    setSwipeDir('right');
    setTimeout(() => { setMonth(data.previousMonth); setSwipeDir(null); }, 150);
  };

  // Swipe handling
  const onTouchStart = (e) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onTouchEnd = (e) => {
    if (!touchStart.current) return;
    const dx = e.changedTouches[0].clientX - touchStart.current.x;
    const dy = e.changedTouches[0].clientY - touchStart.current.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx > 0) goPrev(); // swipe right = previous (RTL)
      else goNext(); // swipe left = next
    }
    touchStart.current = null;
  };

  const loadDaily = async (dayKey) => {
    try {
      const res = await fetch(`${BASE_URL}/api/donations/daily?dayKey=${dayKey}`, {
        headers: { 'Authorization': `Bearer ${user.credential}` },
      });
      const json = await res.json();
      setDailyPopup({ dayKey, rows: json.rows });
    } catch (e) { console.error(e); }
  };

  const formatNum = (n) => n ? n.toLocaleString('he-IL', { maximumFractionDigits: 0 }) : '0';

  const formatMonthLabel = () => {
    if (!data) return 'טוען...';
    if (calendarType === 'gregorian') {
      return data.currentMonth.split('-').reverse().join(' ');
    }
    return data.currentMonth.split('-').map((p, i) => <div key={i}>{p}</div>);
  };

  const toggleCalendar = () => {
    setCalendarType(prev => prev === 'hebrew' ? 'gregorian' : 'hebrew');
    setMonth('dummy');
  };

  const recurringDiff = data?.recurring ?
    (data.recurring.value - data.recurring.lastYearValue) : 0;
  const recurringPct = data?.recurring?.lastYearValue > 0 ?
    Math.round(((data.recurring.value - data.recurring.lastYearValue) / data.recurring.lastYearValue) * 100) : null;

  return (
    <div
      className={styles.container}
      ref={containerRef}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Header with calendar toggle */}
      <div className={styles.header}>
        <h2>תרומות</h2>
        <button className={styles.calToggle} onClick={toggleCalendar}>
          {calendarType === 'hebrew' ? '📅 לועזי' : '🕎 עברי'}
        </button>
      </div>

      {/* Month selector - clean, no buttons */}
      <div className={styles.monthSelector}>
        <span className={styles.monthArrow} onClick={goPrev}>›</span>
        <div className={styles.monthLabel}>
          {formatMonthLabel()}
        </div>
        <span className={styles.monthArrow} onClick={goNext}>‹</span>
      </div>
      <p className={styles.swipeHint}>החלק לצדדים לניווט</p>

      {/* Toggle */}
      <div className={styles.toggleRow}>
        <label className={styles.toggle}>
          <input type="checkbox" checked={accumulative} onChange={(e) => setAccumulative(e.target.checked)} />
          <span className={styles.toggleSlider}></span>
          מצטבר
        </label>
      </div>

      {/* One-time summary when not accumulative */}
      {data && data.summary && (
        <div className={styles.summaryRow}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>חד פעמי</span>
            <span className={styles.summaryValue}>₪{formatNum(data.summary.value)}</span>
            <span className={styles.summaryCount}>{data.summary.count} תרומות</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>שנה שעברה</span>
            <span className={styles.summaryValue}>₪{formatNum(data.summary.lastYearValue)}</span>
            <span className={styles.summaryCount}>{data.summary.lastYearCount} תרומות</span>
          </div>
        </div>
      )}

      {loading && <div className={styles.loadingBar}><div className={styles.loadingProgress}></div></div>}

      {/* Table */}
      {data && data.rows && (
        <div className={`${styles.tableWrap} ${swipeDir ? styles[`swipe${swipeDir}`] : ''}`}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>יום</th>
                <th>השנה</th>
                <th>שנה שעברה</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i} className={r.value > 0 ? styles.hasValue : ''}>
                  <td className={styles.dayCell}>{r.day}</td>
                  <td className={styles.valueCell} onClick={() => r.key && r.value > 0 && loadDaily(r.key)}>
                    {r.value > 0 ? `₪${formatNum(r.value)}` : '—'}
                    {r.count > 0 && <span className={styles.countBadge}>{r.count}</span>}
                  </td>
                  <td className={styles.lastYearCell} onClick={() => r.lastYearKey && r.lastYearValue > 0 && loadDaily(r.lastYearKey)}>
                    {r.lastYearValue > 0 ? `₪${formatNum(r.lastYearValue)}` : '—'}
                    {r.lastYearCount > 0 && <span className={styles.countBadge}>{r.lastYearCount}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recurring (הו"ק) — secondary, at the bottom */}
      {data?.recurring && data.recurring.value > 0 && (
        <div className={styles.recurringStrip}>
          <span className={styles.recurringStripLabel}>הוראות קבע</span>
          <span className={styles.recurringStripValue}>₪{formatNum(data.recurring.value)}</span>
          <span className={styles.recurringStripCount}>({data.recurring.count})</span>
          <span className={styles.recurringStripSep}>·</span>
          <span className={styles.recurringStripLast}>אשתקד ₪{formatNum(data.recurring.lastYearValue)}</span>
          {recurringPct !== null && (
            <span className={`${styles.recurringStripPct} ${recurringDiff >= 0 ? styles.positive : styles.negative}`}>
              {recurringDiff >= 0 ? '+' : ''}{recurringPct}%
            </span>
          )}
        </div>
      )}

      {/* Daily popup */}
      {dailyPopup && (
        <div className={styles.modal} onClick={() => setDailyPopup(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button className={styles.modalClose} onClick={() => setDailyPopup(null)}>✕</button>
            <h3>פירוט יומי — {dailyPopup.dayKey}</h3>
            <table className={styles.dailyTable}>
              <thead>
                <tr><th>שעה</th><th>סכום</th><th>תורם</th></tr>
              </thead>
              <tbody>
                {dailyPopup.rows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.time}</td>
                    <td>₪{formatNum(r.sum)}</td>
                    <td>{r.method}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
